import React, {useState, useRef, useContext} from 'react';
import {ChevronDown, ChevronUp, Plus} from 'lucide-react';
import {
  connector,
  cup,
  detectAnd,
  detectOr,
  ruleIcon,
  startProcess,
} from 'assets/images/images';
import PreviewButtons from './PreviewButtons';
import {PanZoom} from 'react-easy-panzoom';
import ZoomControls from './ZoomControls';
import {CreditCheckContext} from '../../../../../helpers/context/CreditCheckContext';
import VersionLabel from './VersionLabel';
import {ConditionPreview} from './ConditionPreview';

function PreviewScreen({
  isEditorOpen,
  rules,
  addNewRule,
  noPreviewBtn,
  versionLabel,
  setRuleListLoading,
  isListLoading,
}) {
  const [collapsedRules, setCollapsedRules] = useState({});
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const panZoomRef = useRef(null);
  const actualZoomRef = useRef(1.0);

  const {
    set: {setCurrentActiveRule},
  } = useContext(CreditCheckContext);

  const toggleRuleCollapse = ruleId => {
    setCollapsedRules(prev => ({
      ...prev,
      [ruleId]: !prev[ruleId],
    }));
  };

  const handleAddRuleAt = index => {
    addNewRule(index);
  };

  const resetZoom = () => {
    if (panZoomRef.current) {
      panZoomRef.current.reset();
      panZoomRef.current.autoCenter(1, true);
      actualZoomRef.current = 1.0;
      setZoomLevel(1.0);
    }
  };

  const zoomIn = () => {
    if (panZoomRef.current) {
      panZoomRef.current.zoomIn(1);
    }
  };

  const zoomOut = () => {
    if (panZoomRef.current) {
      panZoomRef.current.zoomOut(1);
    }
  };

  return (
    <main
      className={`bg-[#DAE4F4] transition-all relative duration-300 ease-in-out w-full ${
        isEditorOpen ? 'ml-[394px]' : 'ml-0'
      }`}
      style={{
        height: 'calc(100vh - 120px)',
        overflowY: 'hidden',
        opacity: isListLoading && 0.3,
      }}
    >
      {versionLabel && <VersionLabel version={versionLabel} />}

      <PanZoom
        autoCenter={true}
        zoomSpeed={0.1}
        minZoom={0.5}
        ref={panZoomRef}
        onStateChange={state => {
          if (state && typeof state.scale === 'number') {
            const newZoom = Number(state.scale.toFixed(2));
            if (newZoom !== actualZoomRef.current) {
              actualZoomRef.current = newZoom;
              setZoomLevel(newZoom);
            }
          }
        }}
      >
        <div className="flex flex-col items-center mt-20">
          <div className="mb-4 bg-white p-2 rounded-lg border border-[#9FBDED] shadow-sm">
            <img src={startProcess} alt="Start Process" />
          </div>

          <button
            className="bg-secondaryBlue rounded-full p-1 text-white mb-2"
            onClick={() => handleAddRuleAt(0)}
          >
            <Plus className="w-2 h-2" />
          </button>

          {rules.length === 0 ? (
            <>
              <img src={connector} className="mb-2" alt="Connector" />
              <button
                className="bg-secondaryBlue rounded-full p-1 text-white mb-2"
                onClick={() => handleAddRuleAt(0)}
              >
                <Plus className="w-2 h-2" />
              </button>
            </>
          ) : (
            rules.map((rule, index) => (
              <React.Fragment key={rule.id}>
                <div className="relative mb-2 transition-all duration-200">
                  <div className="bg-[#EEF3FC] border border-[#CED2DD] rounded-lg overflow-hidden">
                    <div
                      className="flex items-center bg-[#EEF3FC] p-4 cursor-pointer gap-5 transition-all duration-200 ease-in-out"
                      onClick={() => {
                        toggleRuleCollapse(rule.id);
                        !versionLabel &&
                          setCurrentActiveRule({
                            ...rule,
                            isRuleOpen: true,
                          });
                      }}
                    >
                      <div className="flex items-center gap-[10px]">
                        <img src={ruleIcon} alt="Rule Icon" />
                        <span className="font-medium   text-[#033971]">
                          {rule.name}
                        </span>
                      </div>
                      <div className="ml-auto flex items-center transition-all duration-200">
                        {collapsedRules[rule.id] ? (
                          <ChevronDown className="w-5 h-5 fill-black stroke-none" />
                        ) : (
                          <ChevronUp className="w-5 h-5 fill-black stroke-none" />
                        )}
                      </div>
                    </div>

                    {!collapsedRules[rule.id] &&
                      rule.groups.some(group =>
                        group.conditions.some(
                          condition =>
                            condition.field ||
                            condition.operator ||
                            condition.value,
                        ),
                      ) && (
                        <div className="p-4">
                          <div
                            className={`${
                              rule.conditionType?.toLowerCase() === 'or'
                                ? 'flex flex-row items-center'
                                : 'flex flex-col items-center'
                            } transition-all duration-200`}
                            onClick={() => {
                              !versionLabel &&
                                setCurrentActiveRule({
                                  ...rule,
                                  isRuleOpen: true,
                                });
                            }}
                          >
                            {rule.groups.map((group, groupIndex) => (
                              <React.Fragment key={group.id}>
                                <div className="border border-dashed border-[#858DAA] bg-white rounded-lg p-3 h-fit w-fit transition-all duration-200">
                                  <div
                                    className={
                                      group.conditionType.toLowerCase() === 'or'
                                        ? 'flex flex-row items-center'
                                        : 'flex flex-col items-center'
                                    }
                                  >
                                    {group.conditions.map(
                                      (condition, condIndex) => (
                                        <ConditionPreview
                                          key={condition.id}
                                          condition={condition}
                                          rule={rule}
                                          condIndex={condIndex}
                                          group={group}
                                          setListLoading={setRuleListLoading}
                                        />
                                      ),
                                    )}
                                  </div>
                                </div>
                                {groupIndex < rule.groups.length - 1 && (
                                  <div className="flex items-center justify-center">
                                    <span className="m-[1px]">
                                      {rule.conditionType === 'and' ? (
                                        <img src={detectAnd} alt="Image" />
                                      ) : (
                                        <img src={detectOr} alt="Image" />
                                      )}
                                    </span>
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                <button
                  className="bg-secondaryBlue rounded-full p-1 text-white mb-2"
                  onClick={() => handleAddRuleAt(index + 1)}
                >
                  <Plus className="w-2 h-2" />
                </button>

                {index < rules.length - 1 ? (
                  <>
                    <img src={connector} className="mb-2" alt="Connector" />
                    <button
                      className="bg-secondaryBlue rounded-full p-1 text-white mb-2"
                      onClick={() => handleAddRuleAt(index + 1)}
                    >
                      <Plus className="w-2 h-2" />
                    </button>
                  </>
                ) : (
                  <>
                    <img src={connector} className="mb-2" alt="Connector" />
                    <button
                      className="bg-secondaryBlue rounded-full p-1 text-white mb-2"
                      onClick={() => handleAddRuleAt(index + 1)}
                    >
                      <Plus className="w-2 h-2" />
                    </button>
                  </>
                )}
              </React.Fragment>
            ))
          )}

          <div className="bg-white p-2 rounded-lg border border-[#9FBDED] shadow-sm">
            <img src={cup} alt="Cup" />
          </div>
        </div>
      </PanZoom>

      {!noPreviewBtn && (
        <div className="absolute bottom-12 left-4">
          <PreviewButtons />
        </div>
      )}

      <div className="absolute top-4 right-4">
        <ZoomControls
          zoomLevel={zoomLevel}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetZoom}
        />
      </div>
    </main>
  );
}

export default PreviewScreen;
